import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Collapse,
    Button,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { colors } from 'features/userData/style';
import AddrCoordToggle from './common/AddrCoordToggle';
import {
    Section,
    SectionTitle,
    SectionBody,
    InputForm
} from 'features/geocoder/components/common/BasicComponents';
import DeleteFileModal from './common/DeleteFileModal';

const FileListItem = ({ 
    executionSummary,
    onSettingClick,
    showReverse,
    setShowReverse,
    clearUploadedData,
    encodingOptions,
    encoding,
    onEncodingChange,
    delimiterOptions,
    delimiter,
    onDelimiterChange,
    showDelimiterOption,
    unifiedAddrOptions,
    unifiedAddrField,
    onAddrFieldChange,
    xyFieldOptions,
    xField,
    onXFieldChange,
    yField,
    onYFieldChange,
    geocodingTypeOptions,
    geocodingType,
    onGeocodingTypeChange,
    coordSystemOptions,
    coordSystem,
    onCoordSystemChange,
    downloadCoordSystem,
    onDownloadCoordSystemChange,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleConfirmDelete,
    progress
}) => {
    
    // 파일 상세 보기(확장) 여부
    const [expanded, setExpanded] = useState(true);
    
    // 상세 설정 펼침 여부
    const [showMoreOptions, setShowMoreOptions] = useState(false);

    // 입력창 활성화 여부
    const [inputDisabled, setInputDisabled] = useState(false);

    // 지오코딩 진행 중 입력창 비활성화
    useEffect(() => {
        if (progress >= 0 && progress < 100) setInputDisabled(true);
        if (progress === -1 || progress === 100) setInputDisabled(false);
    }, [progress]);

    if (!executionSummary || !Array.isArray(executionSummary) || executionSummary.length === 0) {
        // 데이터가 없으면 null을 반환하여 렌더링 중단
        return null; 
    }

    const fullFileName = executionSummary.find(item => item.label === "파일명").value;
    const fileName = fullFileName.slice(0, fullFileName.lastIndexOf('.'));
    const extension = fullFileName.slice(fullFileName.lastIndexOf('.') + 1).toUpperCase();

    const fileDetails = [
        { label: '파일명', value: fullFileName },
        { label: '파일 크기', value: executionSummary.find(item => item.label === "파일 크기").value },
        { label: '행', value: `${executionSummary.find(item => item.label === "행").value}행` },
    ];

    const handleExpandToggle = () => setExpanded(!expanded);
    const handleMoreOptionsToggle = () => setShowMoreOptions(!showMoreOptions);

    return (
        <Paper 
            variant="outlined"
            elevation={0}
            sx={{ 
                mb: 1, 
                // borderColor: '#ddd',
                border: 0,
                borderRadius: 0,
                boxShadow: 'none',
                overflow: 'hidden',
                transition: 'border-color 0.3s',
                
            }}
        >
            {/* 파일 정보 및 액션 버튼 영역 */}
            <Box 
                sx={{ 
                    position: 'relative',
                    display: 'flex', 
                    alignItems: 'center', 
                    p: 1.5,
                    pl: 3,
                    borderTop: '1px solid #ddd',
                    borderRight: '1px solid #ddd',
                    borderBottom: '1px solid #ddd',
                    bgcolor: 'transparent',
                    mt: 3,
                    mb: 1.5,

                    // 가상 요소 스타일: 네이비 포인트 바
                    '&::before': {
                        content: '""', // 가상 요소는 content 속성 필요
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        width: '8px',
                        backgroundColor: colors.darkBlue,
                    }
                }}
            >
                {/* 파일명 및 확장자 */}
                <Box sx={{ flexGrow: 1, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 500, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            maxWidth: '90%' // 버튼 영역 침범 방지
                        }}
                        title={fileName}
                    >
                        {fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                        {extension}
                    </Typography>
                </Box>
                
                {/* 액션 버튼 그룹 */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    
                    {/* 삭제 버튼 */}
                    <IconButton 
                        size="small" 
                        onClick={() => setIsDeleteModalOpen(true)}
                        title="파일 삭제"
                    >
                        <DeleteIcon fontSize="small" sx={{ color: '#666' }} />
                    </IconButton>
                    
                    {/* 설정 버튼 */}
                    <IconButton 
                        size="small" 
                        onClick={onSettingClick}
                        title="파일 설정"
                    >
                        <SettingsIcon fontSize="small" sx={{ color: '#666' }} />
                    </IconButton>
                    
                    {/* 펼침/접기 버튼 */}
                    <IconButton 
                        size="small" 
                        onClick={handleExpandToggle}
                        title={expanded ? "접기" : "펼치기"}
                    >
                        {expanded ? (
                            <KeyboardArrowUpIcon fontSize="small" sx={{ color: '#666' }} />
                        ) : (
                            <KeyboardArrowDownIcon fontSize="small" sx={{ color: '#666' }} />
                        )}
                    </IconButton>
                </Box>
            </Box>
            
            {/* 상세 설정 내용 (펼침 영역) */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: '2 2 0 2', bgcolor: '#ffffff' }}>
                    <Section>
                        <SectionTitle>
                            파일 정보
                            <Tooltip 
                                title={"업로드한 파일의 정보를 표시합니다."}
                                placement="right"
                                arrow
                            >
                                <HelpOutlineIcon 
                                    fontSize="small" 
                                    sx={{ 
                                        color: 'primary.main',
                                        cursor: 'pointer',
                                        ml: 1,
                                        mb: 0.1
                                    }} 
                                    />
                            </Tooltip>
                        </SectionTitle>
                        <SectionBody>
                            {fileDetails.map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: '#333', 
                                            width: 100, 
                                            fontWeight: 500,
                                            flexShrink: 0,
                                            mr: 2,
                                        }}>
                                        {item.label}
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontWeight: 400, 
                                            color: '#333',
                                            flexGrow: 1, 
                                            textAlign: 'right', 
                                            wordBreak: 'break-word', 
                                            whiteSpace: 'normal', 
                                        }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </SectionBody>
                    </Section>
                    <Section>
                        <Box
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                            }}
                        >
                            <SectionTitle>
                                지오코딩 설정
                                <Tooltip 
                                    title={"지도 하단의 데이터 미리보기를 참고하여 설정해 주세요."}
                                    placement="right"
                                    arrow
                                >
                                    <HelpOutlineIcon 
                                        fontSize="small" 
                                        sx={{ 
                                            color: 'primary.main',
                                            cursor: 'pointer',
                                            ml: 1,
                                            mb: 0.1
                                        }} 
                                        />
                                </Tooltip>
                            </SectionTitle>
                           
                            {/* 주소/좌표 입력 토글 버튼 */}
                            <AddrCoordToggle 
                                showReverse={showReverse} 
                                setShowReverse={setShowReverse} 
                            />
                        </Box>

                        <SectionBody>
                            {showReverse ? (
                                <>
                                    <InputForm 
                                        label="X 좌표 필드" 
                                        selectVal={xField}
                                        options={xyFieldOptions} 
                                        menuItemKey={(opt) => `${opt.idx}-${opt.value}`}
                                        onChange={onXFieldChange}
                                        tooltip="데이터의 X 좌표(경도) 열을 선택해 주세요."
                                        disabled={inputDisabled}
                                    />
        
                                    <InputForm
                                        label="Y 좌표 필드" 
                                        selectVal={yField}
                                        options={xyFieldOptions} 
                                        menuItemKey={(opt) => `${opt.idx}-${opt.value}`}
                                        onChange={onYFieldChange}
                                        tooltip="데이터의 Y 좌표(위도) 열을 선택해 주세요."
                                        disabled={inputDisabled}
                                    />
                                </>
                            ) : (
                                <>
                                    <InputForm
                                        label="주소 필드" 
                                        selectVal={unifiedAddrField}
                                        options={unifiedAddrOptions} 
                                        menuItemKey="idx" 
                                        onChange={onAddrFieldChange}
                                        tooltip="데이터의 주소(위치) 열을 선택해 주세요."
                                        disabled={inputDisabled}
                                    />
        
                                    <InputForm
                                        label="주소 유형" 
                                        selectVal={geocodingType}
                                        options={geocodingTypeOptions} 
                                        menuItemKey="name" 
                                        onChange={onGeocodingTypeChange}
                                        tooltip="주소의 유형(도로명/지번)을 선택해 주세요."
                                        disabled={inputDisabled}
                                    />
                                </>
                            )}
                            <Collapse in={showMoreOptions} timeout="auto" unmountOnExit>
                                <InputForm 
                                    label="인코딩 방식" 
                                    selectVal={encoding}
                                    options={encodingOptions} 
                                    menuItemKey="name" 
                                    onChange={onEncodingChange}
                                    tooltip="글자 깨짐 현상 발생 시 인코딩 방식을 변경해 주세요."
                                    disabled={inputDisabled}
                                />
                                <InputForm 
                                    label="구분자" 
                                    selectVal={delimiter}
                                    options={delimiterOptions} 
                                    menuItemKey="name" 
                                    onChange={onDelimiterChange}
                                    tooltip="CSV 파일의 경우 기본 구분자는 콤마(,) 입니다."
                                    disabled={inputDisabled}
                                />
                                <InputForm 
                                    label="좌표계" 
                                    selectVal={coordSystem}
                                    options={coordSystemOptions} 
                                    menuItemKey="name" 
                                    onChange={onCoordSystemChange}
                                    tooltip="데이터의 기준 좌표계를 선택해 주세요."
                                    disabled={inputDisabled}
                                />
                            </Collapse>
                            
                            {/* 설정 옵션 더 보기 버튼 */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3, pb: 1 }}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={handleMoreOptionsToggle}
                                    endIcon={showMoreOptions ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    sx={{ 
                                        textTransform: 'none',
                                        borderColor: '#D5ECFF', 
                                        bgcolor: '#F3FAFF' 
                                    }}
                                >
                                    설정 옵션 더 보기
                                </Button>
                            </Box>
                        </SectionBody>
                    </Section>

                    <Section>
                        <SectionTitle>
                            다운로드 설정
                            <Tooltip 
                                title={"지오코딩 실행 후 파일을 다운로드할 수 있습니다."}
                                placement="right"
                                arrow
                            >
                                <HelpOutlineIcon 
                                    fontSize="small" 
                                    sx={{ 
                                        color: 'primary.main',
                                        cursor: 'pointer',
                                        ml: 1,
                                    }} 
                                    />
                            </Tooltip>
                        </SectionTitle>
                        <SectionBody sx={{ mb: 0, pb: 0}}>
                            {showReverse ? (
                                <InputForm 
                                    label="주소 유형" 
                                    selectVal={geocodingType}
                                    // options={} 
                                    menuItemKey="name"
                                    // onChange={}
                                    tooltip="다운로드할 파일에 적용될 주소 유형을 선택해 주세요."
                                    disabled={inputDisabled}
                                />
                            ) : (
                                <InputForm
                                    label="좌표계" 
                                    selectVal={downloadCoordSystem}
                                    options={coordSystemOptions} 
                                    menuItemKey="name"
                                    onChange={onDownloadCoordSystemChange}
                                    tooltip="다운로드할 파일에 적용될 좌표계를 선택해 주세요."
                                    disabled={inputDisabled}
                                />
                            )}
                        </SectionBody>
                    </Section>
                </Box>

                <DeleteFileModal 
                    isDeleteModalOpen={isDeleteModalOpen}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    handleConfirmDelete={handleConfirmDelete}
                />

            </Collapse>
        </Paper>
    );
};

export default FileListItem;